import { useEffect, useState } from "react";
import { api, yen, PAYMENTS, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { DatePicker } from "../components/DatePicker";
import { VisitorsCard } from "../components/VisitorsCard";
import { BuyerMapCard } from "../components/BuyerMapCard";
import { jstToday, jstMonth } from "../lib/jst";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { TrendingUp, ShoppingCart, Wallet, Package, Download, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const Kpi = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-muted-foreground">{label}</p>
      <Icon size={18} className="text-primary" />
    </div>
    <p className="num text-2xl lg:text-3xl font-semibold">{value}</p>
  </div>
);

export default function Laporan() {
  const { t } = useI18n();
  const [r, setR] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [month, setMonth] = useState(jstMonth);
  const today = jstToday();
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);

  useEffect(() => {
    api.get("/reports/summary", { params: { start, end } }).then(({ data }) => setR(data));
    api.get("/shifts").then(({ data }) => setShifts(data)).catch(() => {});
  }, [start, end]);

  const invalidRange = start && end && end < start;

  if (!r) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold">{t("laporan_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("laporan_sub")}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("from")}</p>
            <DatePicker
              testid="export-start-input"
              value={start}
              onChange={setStart}
              placeholder={t("from")}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("to")}</p>
            <DatePicker
              testid="export-end-input"
              value={end}
              onChange={setEnd}
              placeholder={t("to")}
            />
          </div>
          <Button
            data-testid="export-report-btn"
            onClick={async () => {
              if (start && end && end < start) return toast.error(t("date_range_invalid"));
              try {
                const { data } = await api.get("/reports/export", {
                  params: { start, end },
                  responseType: "blob",
                });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `laporan-penjualan-${start}-${end}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              } catch {
                toast.error("Gagal mengunduh laporan");
              }
            }}
          >
            <Download size={16} className="mr-1" /> {t("export")}
          </Button>
          <div className="flex items-center gap-2">
            <input
              type="month"
              data-testid="monthly-export-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm"
            />
            <Button
              variant="outline"
              data-testid="monthly-export-btn"
              onClick={async () => {
                try {
                  const { data } = await api.get("/reports/monthly-export", {
                    params: { month },
                    responseType: "blob",
                  });
                  const url = URL.createObjectURL(data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `laporan-bulanan-${month}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("Gagal mengunduh laporan bulanan");
                }
              }}
            >
              <Download size={16} className="mr-1" /> Laporan Bulanan
            </Button>
          </div>
          <Button
            variant="outline"
            data-testid="send-telegram-btn"
            onClick={async () => {
              try {
                await api.post("/reports/telegram", null, { params: { start, end } });
                toast.success("Laporan terkirim ke Telegram");
              } catch (e) {
                toast.error(errMsg(e, "Gagal mengirim ke Telegram"));
              }
            }}
          >
            <Send size={16} className="mr-1" /> Kirim ke Telegram
          </Button>
        </div>
      </div>

      {invalidRange && (
        <p className="text-sm text-primary" data-testid="date-range-warning">
          Tanggal "Sampai" lebih awal dari "Dari" — data periode akan kosong.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Penjualan Periode" value={yen(r.range_revenue)} icon={TrendingUp} testid="kpi-range-revenue" />
        <Kpi label="Transaksi Periode" value={r.range_orders} icon={ShoppingCart} testid="kpi-range-orders" />
        <Kpi label={t("today_revenue")} value={yen(r.today_revenue)} icon={Wallet} testid="kpi-today-revenue" />
        <Kpi label="Belanja Stok Periode" value={yen(r.range_purchase || 0)} icon={Package} testid="kpi-range-purchase" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-5">
          <p className="font-display font-semibold mb-4">
            Penjualan per Hari{" "}
            <span className="text-xs text-muted-foreground font-normal">
              ({start} — {end})
            </span>
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={r.daily}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} width={50} />
                <Tooltip formatter={(v) => yen(v)} />
                <Bar dataKey="total" fill="#FF6B6B" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="font-display font-semibold mb-4">{t("top_items")}</p>
          <div className="space-y-3">
            {r.top_items.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("no_data")}</p>
            )}
            {r.top_items.map((it, i) => (
              <div key={it.name} className="flex justify-between text-sm" data-testid={`top-item-${i}`}>
                <span className="truncate pr-2">
                  <span className="text-muted-foreground num mr-2">{i + 1}.</span>
                  {it.name}
                </span>
                <span className="num shrink-0">{yen(it.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="font-display font-semibold mb-4">{t("by_method")}</p>
          <div className="space-y-3">
            {PAYMENTS.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`method-total-${p.id}`}>
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                  {t(`pm_${p.id}`)}
                </span>
                <span className="num">{yen(r.by_method[p.id] || 0)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="by-cashier-card">
          <p className="font-display font-semibold mb-4">Penjualan per Kasir</p>
          <p className="text-xs text-muted-foreground -mt-3 mb-3">
            {start} — {end}
          </p>
          <div className="space-y-3">
            {(!r.by_cashier || r.by_cashier.length === 0) && (
              <p className="text-sm text-muted-foreground">{t("no_data")}</p>
            )}
            {(r.by_cashier || []).map((c, i) => (
              <div key={c.name} className="flex justify-between text-sm" data-testid={`cashier-row-${i}`}>
                <span className="truncate pr-2">
                  {c.name}
                  <span className="text-muted-foreground ml-2 num">{c.count} trx</span>
                </span>
                <span className="num shrink-0">{yen(c.total)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="font-display font-semibold mb-4">{t("low_stock")}</p>
          <div className="space-y-2" data-testid="low-stock-list">
            {r.low_stock.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("stock_ok")}</p>
            )}
            {r.low_stock.map((p) => (
              <div key={p.id} className="flex justify-between text-sm">
                <span>{p.name}</span>
                <span className="num text-amber-600 font-semibold">
                  {p.stock} {p.unit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <p className="font-display font-semibold mb-4">
          Pengunjung per Hari{" "}
          <span className="text-xs text-muted-foreground font-normal">
            ({start} — {end})
          </span>
        </p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={visits}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis
                tickLine={false}
                axisLine={false}
                fontSize={12}
                width={40}
                allowDecimals={false}
              />
              <Tooltip formatter={(v) => `${v} kunjungan`} />
              <Bar dataKey="count" fill="#107C56" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <VisitorsCard start={start} end={end} onData={setVisits} />

      <BuyerMapCard start={start} end={end} />

      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="shift-history-card">
        <p className="font-display font-semibold mb-4">Riwayat Tutup Kas</p>
        <div className="space-y-2">
          {shifts.length === 0 && (
            <p className="text-sm text-muted-foreground">Belum ada penutupan kas.</p>
          )}
          {shifts.map((s) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-2 text-sm bg-gray-50 rounded-xl px-3 py-2"
              data-testid={`shift-row-${s.id}`}
            >
              <span className="min-w-0">
                <span className="font-medium">{s.date}</span>
                <span className="text-muted-foreground"> · {s.cashier_name}</span>
                <span className="text-muted-foreground num"> · {s.order_count} trx</span>
              </span>
              <span className="flex items-center gap-4">
                <span className="num">{yen(s.revenue)}</span>
                <span
                  className={`num text-xs font-semibold ${
                    s.difference === 0 ? "text-emerald-600" : "text-primary"
                  }`}
                >
                  selisih {s.difference > 0 ? "+" : ""}
                  {yen(s.difference)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
